import React, { useCallback, useEffect, useState } from "react";
import {
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Button,
    Typography,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    IconButton,
    Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import configData from "../config.json";
import { useNavigate } from "react-router-dom";
import { getToken } from "../utils/auth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ROLE_API = `${configData.SERVER_URL}/MDM/Role_Management`;

export default function PermissionManagement() {
    const navigate = useNavigate();
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
    const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);

    const [newRoleName, setNewRoleName] = useState("");
    const [roleToDelete, setRoleToDelete] = useState("");

    const authHeaders = () => ({
        "Content-Type": "application/json",
        "x-access-token": getToken(),
    });

    const applyMatrixData = (data) => {
        setRoles(Array.isArray(data?.roles) ? data.roles : []);
        setPermissions(Array.isArray(data?.permissions) ? data.permissions : []);
        setHasChanges(false);
    };

    const fetchMatrix = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(ROLE_API, {
                method: "GET",
                headers: authHeaders(),
            });

            if (!res.ok) {
                navigate("/");
                return;
            }

            const data = await res.json();
            applyMatrixData(data);
        } catch (error) {
            toast.error("Failed to load roles and permissions");
            navigate("/");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchMatrix();
    }, [fetchMatrix]);

    const filteredPermissions = permissions.filter((permission) =>
        permission.permission_name.toLowerCase().includes(search.toLowerCase())
    );

    const handleToggle = (permissionName, roleName) => {
        setPermissions((prev) =>
            prev.map((permission) => {
                if (permission.permission_name !== permissionName) {
                    return permission;
                }
                return {
                    ...permission,
                    [roleName]: !permission[roleName],
                };
            })
        );
        setHasChanges(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetch(ROLE_API, {
                method: "PUT",
                headers: authHeaders(),
                body: JSON.stringify({ permissions }),
            });

            const data = await res.json();
            if (!res.ok) {
                toast.error(data.message || "Failed to save permissions matrix");
                return;
            }

            applyMatrixData(data);
            toast.success("Permissions matrix saved");
        } catch (error) {
            toast.error("Failed to save permissions matrix");
        } finally {
            setSaving(false);
        }
    };

    const handleAddRole = async () => {
        const roleName = newRoleName.trim();
        if (!roleName) {
            toast.error("Role name is required");
            return;
        }

        try {
            const res = await fetch(ROLE_API, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ roleName }),
            });
            const data = await res.json();

            if (!res.ok) {
                toast.error(data.message || "Failed to add role");
                return;
            }

            applyMatrixData(data);
            setAddRoleDialogOpen(false);
            setNewRoleName("");
            toast.success(`Role "${roleName}" added`);
        } catch (error) {
            toast.error("Failed to add role");
        }
    };

    const handleDeleteRole = async () => {
        if (!roleToDelete) {
            return;
        }

        const deletedRole = roleToDelete;

        try {
            const res = await fetch(ROLE_API, {
                method: "DELETE",
                headers: authHeaders(),
                body: JSON.stringify({ roleName: roleToDelete }),
            });
            const data = await res.json();

            if (!res.ok) {
                toast.error(data.message || "Failed to delete role");
                return;
            }

            applyMatrixData(data);
            setDeleteRoleDialogOpen(false);
            setRoleToDelete("");
            toast.success(`Role "${deletedRole}" deleted`);
        } catch (error) {
            toast.error("Failed to delete role");
        }
    };

    return (
        <Paper
            className="p-6"
            style={{ height: "90vh", display: "flex", flexDirection: "column", padding: 24 }}
        >
            <ToastContainer position="top-right" autoClose={3000} />

            <Typography variant="h5" gutterBottom>
                Permission Management
            </Typography>

            <div
                style={{
                    display: "flex",
                    gap: 12,
                    marginBottom: 16,
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <TextField
                    label="Search permissions"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                />
                <Button variant="outlined" onClick={() => setAddRoleDialogOpen(true)}>
                    Add Role
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={!hasChanges || saving || loading}
                >
                    {saving ? "Saving..." : "Save Changes"}
                </Button>
            </div>

            <div
                style={{
                    flex: 1,
                    overflow: "auto",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                }}
            >
                {loading ? (
                    <Typography style={{ padding: 16 }}>Loading...</Typography>
                ) : (
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    style={{
                                        minWidth: 220,
                                        fontWeight: 600,
                                        backgroundColor: "#fafafa",
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 3,
                                    }}
                                >
                                    Permission
                                </TableCell>
                                {roles.map((role) => (
                                    <TableCell
                                        key={role}
                                        align="center"
                                        style={{ minWidth: 140, fontWeight: 600, backgroundColor: "#fafafa" }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                                            <span>{role}</span>
                                            <Tooltip title={`Delete role "${role}"`}>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        setRoleToDelete(role);
                                                        setDeleteRoleDialogOpen(true);
                                                    }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPermissions.map((permission) => (
                                <TableRow key={permission.permission_name} hover>
                                    <TableCell
                                        style={{
                                            fontWeight: 500,
                                            position: "sticky",
                                            left: 0,
                                            backgroundColor: "#fff",
                                            zIndex: 1,
                                        }}
                                    >
                                        {permission.permission_name}
                                    </TableCell>
                                    {roles.map((role) => (
                                        <TableCell key={`${permission.permission_name}-${role}`} align="center">
                                            <Checkbox
                                                checked={Boolean(permission[role])}
                                                onChange={() =>
                                                    handleToggle(permission.permission_name, role)
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            {!filteredPermissions.length && (
                                <TableRow>
                                    <TableCell colSpan={roles.length + 1} align="center">
                                        No permissions found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </div>

            <Dialog open={addRoleDialogOpen} onClose={() => setAddRoleDialogOpen(false)}>
                <DialogTitle>Add Role</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Role name"
                        fullWidth
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddRoleDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddRole}>
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteRoleDialogOpen} onClose={() => setDeleteRoleDialogOpen(false)}>
                <DialogTitle>Delete Role</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete role <strong>{roleToDelete}</strong>?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteRoleDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteRole}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
